import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Edit2, Trash2, Save, ArrowUp, ArrowDown, Eye, EyeOff } from 'lucide-react';
import { adminApi, publicApi } from '../../api';
import { PageHeader, Table, Modal, Field, Input, I18nInputs, ImageUpload } from './AdminUI';

const EMPTY = { key: '', label: {it:'',en:''}, desc: {it:'',en:''}, image: '', icon: '', order: 0, active: true, city_slugs: [] };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [cities, setCities] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);

  const load = () => { setLoading(true); adminApi.categories().then(setItems).finally(() => setLoading(false)); };
  useEffect(() => { load(); publicApi.cities().then(setCities); }, []);

  const rows = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.key || '').localeCompare(b.key || '')),
    [items]
  );

  const moveRow = async (row, dir) => {
    const idx = rows.findIndex(r => r.id === row.id);
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const other = rows[j];
    setReordering(true);
    try {
      let oa = row.order ?? 0, ob = other.order ?? 0;
      if (oa === ob) { oa = idx; ob = j; }
      await Promise.all([
        adminApi.updateCategory(row.id, { ...row, order: ob }),
        adminApi.updateCategory(other.id, { ...other, order: oa }),
      ]);
      load();
    } catch (e) { alert('Errore riordinamento: ' + (e.response?.data?.detail || e.message)); }
    finally { setReordering(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...editing, order: Number(editing.order) || 0 };
      if (editing.id) await adminApi.updateCategory(editing.id, payload);
      else await adminApi.createCategory(payload);
      setEditing(null); load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const del = async (row) => {
    if (!window.confirm(`Eliminare la categoria "${row.key}"?`)) return;
    await adminApi.deleteCategory(row.id); load();
  };

  const toggleVisibility = async (row) => {
    await adminApi.toggleCategoryVisibility(row.id); load();
  };

  return (
    <div>
      <PageHeader title="Categorie" subtitle={`${items.length} categorie configurate`} action={<button onClick={() => setEditing({ ...EMPTY })} className="btn-primary flex items-center gap-2"><Plus size={16}/> Nuova</button>} />
      <Table
        draggable
        onReorder={async (next) => {
          setItems(next); // optimistic
          try {
            await adminApi.reorderCategories(next.map(x => x.id));
          } catch (e) { alert('Errore riordino: ' + (e.response?.data?.detail || e.message)); load(); }
        }}
        columns={[
          { key: 'order', label: '#', render: r => <span className="font-mono text-xs">{r.order}</span> },
          { key: 'key', label: 'Key' },
          { key: 'label', label: 'Nome (IT)', render: r => <span className="font-semibold">{r.label?.it || r.label?.en || '—'}</span> },
          { key: 'city_slugs', label: 'Città', render: r => {
              const list = r.city_slugs || [];
              if (list.length === 0) return <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">Tutte</span>;
              return (
                <div className="flex flex-wrap gap-1">
                  {list.map(s => <span key={s} className="px-2 py-0.5 rounded bg-gray-100 text-xs">{s}</span>)}
                </div>
              );
            }
          },
          { key: 'image', label: 'Immagine', render: r => r.image ? <img src={r.image} alt="" className="w-16 h-10 object-cover rounded" /> : '—' },
          { key: 'active', label: 'Stato', render: r => (
            <button
              onClick={() => toggleVisibility(r)}
              data-testid={`cat-toggle-${r.id}`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${r.active !== false ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-gray-200 text-gray-500 hover:bg-gray-300'}`}
              title={r.active !== false ? 'Clicca per nascondere' : 'Clicca per rendere visibile'}
            >
              {r.active !== false ? <><Eye size={12}/> Attiva</> : <><EyeOff size={12}/> Nascosta</>}
            </button>
          )}
        ]}
        rows={rows}
        actions={row => {
          const idx = rows.findIndex(r => r.id === row.id);
          return (
          <div className="flex gap-1 justify-end items-center">
            <button onClick={() => moveRow(row, -1)} disabled={reordering || idx <= 0} title="Sposta su" className="p-1.5 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"><ArrowUp size={14}/></button>
            <button onClick={() => moveRow(row, 1)} disabled={reordering || idx >= rows.length - 1} title="Sposta giù" className="p-1.5 hover:bg-gray-100 rounded text-gray-500 disabled:opacity-30"><ArrowDown size={14}/></button>
            <button onClick={() => setEditing({...row})} className="p-1.5 hover:bg-gray-100 rounded text-blue-600"><Edit2 size={15}/></button>
            <button onClick={() => del(row)} className="p-1.5 hover:bg-gray-100 rounded text-red-600"><Trash2 size={15}/></button>
          </div>
        );}}
      />

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? 'Modifica categoria' : 'Nuova categoria'}>
        {editing && (
          <form onSubmit={save}>
            <Field label="Key (URL slug)" hint="es. ristoranti (solo lettere, numeri, trattini)">
              <Input required disabled={!!editing.id} value={editing.key} onChange={e => setEditing({...editing, key: e.target.value})} placeholder="ristoranti" />
            </Field>
            <Field label="Nome (per lingua)"><I18nInputs value={editing.label} onChange={v => setEditing({...editing, label: v})} /></Field>
            <Field label="Descrizione (per lingua)"><I18nInputs kind="textarea" value={editing.desc} onChange={v => setEditing({...editing, desc: v})} /></Field>
            <Field label="Immagine copertina"><ImageUpload value={editing.image} onChange={v => setEditing({...editing, image: v})} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Icona (lucide)"><Input value={editing.icon || ''} onChange={e => setEditing({...editing, icon: e.target.value})} placeholder="UtensilsCrossed" /></Field>
              <Field label="Ordine"><Input type="number" value={editing.order} onChange={e => setEditing({...editing, order: e.target.value})} /></Field>
            </div>
            <Field label="Città in cui mostrare la categoria" hint="Nessuna selezione = tutte le città. Seleziona una o più città per limitare la visibilità.">
              <div className="flex flex-wrap gap-2 pt-1" data-testid="cat-city-selector">
                <button
                  type="button"
                  onClick={() => setEditing({...editing, city_slugs: []})}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    (editing.city_slugs || []).length === 0
                      ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-white'
                      : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  🌐 Tutte le città
                </button>
                {cities.map(c => {
                  const active = (editing.city_slugs || []).includes(c.slug);
                  return (
                    <button
                      type="button"
                      key={c.slug}
                      onClick={() => setEditing({
                        ...editing,
                        city_slugs: active
                          ? (editing.city_slugs || []).filter(s => s !== c.slug)
                          : [...(editing.city_slugs || []), c.slug]
                      })}
                      data-testid={`cat-city-${c.slug}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active
                          ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-white'
                          : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {active && '✓ '}{c.name}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Stato">
              <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({...editing, active: e.target.checked})} /> Attiva (visibile sul sito)</label>
            </Field>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">Annulla</button>
              <button type="submit" className="btn-primary flex items-center gap-2"><Save size={16}/> Salva</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
